import {
  CaptionLabel,
  CaptionProps,
  DateRange,
  DayPicker,
  DayPickerBase,
  DayPickerMultipleProps,
  DayPickerProps,
  DayPickerRangeProps,
  DayPickerSingleProps,
  Matcher,
} from 'react-day-picker';
import { ClassNames, Theme, useTheme } from '@emotion/react';
import { DayOfWeek, LocalDate, convert } from 'js-joda';
import { IconButton } from 'omakase';
import { IconChevronSmallLeft, IconChevronSmallRight } from '@tock/icons';
import {
  caption,
  computeClassNames,
  headCellRowCss,
  laterMonthButton,
} from 'omakase/DatePicker/datePickerCssDefs';
import { useMediaQuery } from '@material-ui/core';
import React from 'react';

// This component makes use of styles in _react-day-picker.scss

export interface LocalDateRange {
  from: LocalDate | undefined;
  to?: LocalDate | undefined;
}

export type NumberOfMonthsResponsive = {
  xs: number;
  sm?: number;
};

type DayPickerPassThroughProps = Omit<DayPickerBase, 'numberOfMonths'>;

export type DatePickerPropsBase = {
  /**
   * Initial date (effective month) to show when the date picker is opened, if different from the selected date or today
   */
  initialVisibleDate?: LocalDate;
  /**
   * Earliest date to show as a valid selection, defaults to "TODAY", or can be set to "NO_LIMIT", allowing all previous dates as valid options
   */
  earliestSelectableDate?: LocalDate | 'TODAY' | 'NO_LIMIT';
  /**
   * Latest date to show as a valid selection
   */
  latestSelectableDate?: LocalDate;
  /**
   * Number of months to display above xs / sm screen widths, will default to 1 for all screen widths if unspecified.
   */
  numberOfMonths?: NumberOfMonthsResponsive;
  /**
   * Configuration to be passed through to the underlying react-day-picker
   */
  DayPickerProps?: DayPickerPassThroughProps;
};

type SingleDatePickerProps = {
  selectionType: 'single';
  selectedDate?: LocalDate;
  onDateSelected: (date: LocalDate | undefined) => void;
} & DatePickerPropsBase;

type MultipleDatePickerProps = {
  selectionType: 'multiple';
  selectedDates: LocalDate[];
  onDatesSelected: (dates: LocalDate[]) => void;
} & DatePickerPropsBase;

type DateRangePickerProps = {
  selectionType: 'range';
  selectedRange?: LocalDateRange;
  onDateRangeSelected: (rangeStart: LocalDate | undefined, rangeEnd: LocalDate | undefined) => void;
} & DatePickerPropsBase;

export type DatePickerProps =
  SingleDatePickerProps | MultipleDatePickerProps | DateRangePickerProps;

const makeCustomCaptionComponent = (
  disableClickEarlier: boolean,
  disableClickLater: boolean,
  onClickEarlier: () => void,
  onClickLater: () => void,
  prevIsFocused: React.MutableRefObject<boolean>,
  nextIsFocused: React.MutableRefObject<boolean>
) => {
  // I have to give this an UpperCaseName otherwise the rules-of-hooks eslint
  // checks will complain. :-)
  const MadeComponent: React.FunctionComponent<CaptionProps> = (props) => {
    const prevButtonRef = React.useRef<HTMLButtonElement>(null);
    const nextButtonRef = React.useRef<HTMLButtonElement>(null);

    // On mounting a new "version" of the component, check to see if there
    // is focus to maintain. We use refs for this, not state.
    React.useEffect(() => {
      if (prevIsFocused.current) {
        if (prevButtonRef.current?.disabled) {
          // prevButton had focus but we're now in the current month and it's
          // no longer focusable. This effectively blurred it, just clean up our
          // notion of its focus.
          prevIsFocused.current = false;
        } else {
          prevButtonRef.current?.focus();
        }
      } else if (nextIsFocused.current) {
        nextButtonRef.current?.focus();
      }
    }, []);

    return (
      <div css={caption}>
        <CaptionLabel {...props} />

        <IconButton
          disabled={disableClickEarlier}
          onClick={onClickEarlier}
          variant="basic"
          size="medium"
          onFocus={() => (prevIsFocused.current = true)}
          onBlur={() => (prevIsFocused.current = false)}
          aria-label="Pick dates from the previous month"
          ref={prevButtonRef}
        >
          <IconChevronSmallLeft />
        </IconButton>
        <IconButton
          disabled={disableClickLater}
          css={laterMonthButton}
          onClick={onClickLater}
          variant="basic"
          size="medium"
          aria-label="Pick dates from the following month"
          onFocus={() => (nextIsFocused.current = true)}
          onBlur={() => (nextIsFocused.current = false)}
          ref={nextButtonRef}
        >
          <IconChevronSmallRight />
        </IconButton>
      </div>
    );
  };
  return MadeComponent;
};

const InitialsOnlyHeader = () => {
  return (
    <thead>
      <tr css={headCellRowCss}>
        <th>S</th>
        <th>M</th>
        <th>T</th>
        <th>W</th>
        <th>T</th>
        <th>F</th>
        <th>S</th>
      </tr>
    </thead>
  );
};

/** Converts a whole-day js Date to a LocalDate, admitting the possibility of null input. */
const convertToLocalDateOptional = (date?: Date, assumeFirstOfMonth = false) => {
  return date && convertToLocalDate(date, assumeFirstOfMonth);
};

/** Converts a whole-day js Date to a LocalDate. */
export const convertToLocalDate = (date: Date, assumeFirstOfMonth = false) =>
  LocalDate.of(date.getFullYear(), date.getMonth() + 1, assumeFirstOfMonth ? 1 : date.getDate());

const firstDayModifier = (date: Date, requireSunday = false) => {
  const localDate = convertToLocalDate(date);
  return (
    localDate.dayOfMonth() === 1 && (!requireSunday || localDate.dayOfWeek() === DayOfWeek.SUNDAY)
  );
};

const lastDayModifier = (date: Date, requireSaturday = false) => {
  const localDate = convertToLocalDate(date);
  return (
    localDate.lengthOfMonth() === localDate.dayOfMonth() &&
    (!requireSaturday || localDate.dayOfWeek() === DayOfWeek.SATURDAY)
  );
};

const firstDaySundayModifier = (date: Date) => firstDayModifier(date, true);

const lastDaySaturdayModifier = (date: Date) => lastDayModifier(date, true);

const getFirstSelectedDateByMode = (props: DatePickerProps) => {
  switch (props.selectionType) {
    case 'single':
      return props.selectedDate;
    case 'multiple':
      return props.selectedDates[0];
    case 'range':
      return props.selectedRange?.from;
  }
};

/**
 * Omakase date picker component! This is essentially a customization of the react-day-picker component,
 * heavily adjusting the styling to match Omakase design system colors and desired behaviors.
 *
 * It also translates appropriately from JS Joda dates to native JS Date types as needed.
 */
const DatePicker: React.FunctionComponent<DatePickerProps> = ({
  latestSelectableDate,
  earliestSelectableDate = 'TODAY',
  initialVisibleDate,
  DayPickerProps,
  numberOfMonths = { xs: 1, sm: 1 },
  ...selectionTypeProps
}) => {
  const theme: Theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const todayRef = React.useRef(LocalDate.now());
  const [month, setMonth] = React.useState<LocalDate>(
    initialVisibleDate ?? getFirstSelectedDateByMode(selectionTypeProps) ?? todayRef.current
  );
  const prevIsFocused = React.useRef(false);
  const nextIsFocused = React.useRef(false);

  const effectiveEarliestSelectableDate =
    earliestSelectableDate === 'NO_LIMIT'
      ? undefined
      : earliestSelectableDate === 'TODAY'
        ? todayRef.current
        : earliestSelectableDate;

  const isClickPreviousDisabled =
    !!effectiveEarliestSelectableDate &&
    month.year() <= effectiveEarliestSelectableDate.year() &&
    month.monthValue() <= effectiveEarliestSelectableDate.monthValue();

  const isClickNextDisabled =
    !!latestSelectableDate &&
    month.year() >= latestSelectableDate?.year() &&
    month.monthValue() >= latestSelectableDate?.monthValue();

  const CustomCaption = makeCustomCaptionComponent(
    isClickPreviousDisabled,
    isClickNextDisabled,
    () => setMonth(month.minusMonths(1)),
    () => setMonth(month.plusMonths(1)),
    prevIsFocused,
    nextIsFocused
  );

  const getDisabledDays = (): Matcher[] | undefined => {
    const disabled: Matcher[] = [];

    if (effectiveEarliestSelectableDate) {
      disabled.push({ before: convert(effectiveEarliestSelectableDate).toDate() });
    }

    if (latestSelectableDate) {
      disabled.push({ after: convert(latestSelectableDate).toDate() });
    }
    return disabled;
  };

  const getSelectionTypeProps = () => {
    switch (selectionTypeProps.selectionType) {
      case 'single': {
        const { selectedDate, onDateSelected } = selectionTypeProps;
        const effectiveSelectedDate = selectedDate ? convert(selectedDate).toDate() : undefined;

        const wrappedSetSelectedDay = (selectedDay?: Date) => {
          onDateSelected(convertToLocalDateOptional(selectedDay));
        };

        const singleProps: DayPickerSingleProps = {
          mode: 'single',
          selected: effectiveSelectedDate,
          onSelect: wrappedSetSelectedDay,
        };

        return singleProps;
      }
      case 'multiple': {
        const { selectedDates, onDatesSelected } = selectionTypeProps;

        const effectiveSelectedDates: Date[] = (selectedDates ?? []).map((localDate) =>
          convert(localDate).toDate()
        );

        const wrappedSetSelectedDates = (days: Date[] | undefined, _selectedDate: Date) => {
          const selectedLocalDates = (days ?? []).map((day) => convertToLocalDate(day));
          onDatesSelected?.(selectedLocalDates);
        };

        const multipleProps: DayPickerMultipleProps = {
          mode: 'multiple',
          selected: effectiveSelectedDates,
          onSelect: wrappedSetSelectedDates,
        };

        return multipleProps;
      }
      case 'range': {
        const { selectedRange, onDateRangeSelected } = selectionTypeProps;

        const effectiveSelectedRange: DateRange = {
          from: selectedRange?.from && convert(selectedRange.from).toDate(),
          to: selectedRange?.to && convert(selectedRange.to).toDate(),
        };

        const wrappedSetSelectedRange = (selectedRange?: DateRange) => {
          onDateRangeSelected(
            convertToLocalDateOptional(selectedRange?.from),
            convertToLocalDateOptional(selectedRange?.to)
          );
        };

        const rangeProps: DayPickerRangeProps = {
          mode: 'range',
          selected: effectiveSelectedRange,
          onSelect: wrappedSetSelectedRange,
        };

        return rangeProps;
      }
    }
  };

  return (
    <ClassNames>
      {({ css, theme }) => {
        const commonProps: DayPickerProps = {
          classNames: computeClassNames(css, theme, numberOfMonths),
          components: {
            Head: InitialsOnlyHeader,
            Caption: DayPickerProps?.captionLayout === 'dropdown' ? undefined : CustomCaption,
          },
          disabled: getDisabledDays(),
          month:
            numberOfMonths.xs > 1 && !isDesktop
              ? convert(effectiveEarliestSelectableDate ?? todayRef.current).toDate()
              : convert(month).toDate(),
          // keyboard nav can walk the user through the months, account for this. Remember
          // the months we get from JS date are 0-indexed instead of 1-indexed.
          // This is forward-looking; right now DayPicker does not handle this keyboard
          // navigation properly if it is in controlled mode.
          onMonthChange: (newMonth) => setMonth(convertToLocalDate(newMonth, true)),
          modifiers: {
            first_in_month: firstDayModifier,
            last_in_month: lastDayModifier,
            first_in_month_and_fully_left: firstDaySundayModifier,
            last_in_month_and_fully_right: lastDaySaturdayModifier,
          },
        };

        const selectionTypeProps = {
          ...commonProps,
          ...getSelectionTypeProps(),
        };

        return (
          <DayPicker
            {...selectionTypeProps}
            numberOfMonths={Math.max(...Object.values(numberOfMonths))}
            {...DayPickerProps}
          />
        );
      }}
    </ClassNames>
  );
};

export default DatePicker;
